import React, { useState } from 'react'
import classNames from 'classnames'
import { OverlayTrigger, Popover } from 'react-bootstrap'
import { Button } from '../../../../components/Button/Button'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { type CrossClientQueryEvaluation } from '../../../../models/CrossClient'
import { useAppDispatch } from '../../../../store/hooks'
import { addToast } from '../../../../store/toast/toastSlice'
import {
  CROSS_CLIENT_AI_PHASE_ACCESS_LOADING_MESSAGE,
  CROSS_CLIENT_AI_PHASE_ADMIN_BLOCKED_MESSAGE,
  CROSS_CLIENT_AI_PHASE_BLOCKED_TITLE,
  CROSS_CLIENT_AI_PHASE_PERMISSION_BLOCKED_MESSAGE,
  useCrossClientAiPhaseAccess,
} from '../../utils/crossClientAiPhaseAccess'
import { CcqCards, type CcqCard } from './ccqResultView'
import { CcqAiIndicator } from './CcqAiIndicator'
import { CrossClientQuickActionPopover } from './CrossClientQuickActionPopover'
import {
  CCQ_AI_FOOTER_DONE,
  CCQ_AI_FOOTER_DONE_NOT_IN_RECORDS,
  CCQ_AI_FOOTER_RUNNING,
  CCQ_AI_RUN,
  CCQ_AI_STOP,
  CCQ_AI_STOPPING,
  ccqAiBatchResult,
  ccqAiBatchRunning,
} from './ccqRunCopy'
import { CcqAiBatchSteps, useCcqAi } from './useCcqAi'
import { useCcqAiGate } from './useCcqAiGate'
import { useCcqRun } from './useCcqRun'
import { useCcqCursor } from './useCcqListCursor'
import { useCcqWorkspace } from './useCcqWorkspace'
import { useCrossClientEvaluationActions } from './useCrossClientEvaluationActions'
import styles from './CcqGroupCard.module.scss'
import { CCQ_SECONDARY_BUTTON_CLASS } from './ccqClassNames'

interface CcqAiFooterProps {
  card: CcqCard
}

const SECONDARY = classNames(CCQ_SECONDARY_BUTTON_CLASS, 'text-nowrap')

export const CcqAiFooter: React.FC<CcqAiFooterProps> = ({ card }) => {
  const { isDone } = useCcqRun()
  const { selection, clearSelection } = useCcqWorkspace()
  const fullListCard = useCcqCursor().list.card
  const { batchFor, trackEvaluation, stopBatch } = useCcqAi()
  const { evaluateSelectionSplit } = useCrossClientEvaluationActions(card)
  const { isBlocked, canRetryAccess, tooltipTitle, tooltipContent } = useCcqAiGate()
  const { hasAdminAccess } = useCrossClientAiPhaseAccess()
  const dispatch = useAppDispatch()
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const [isStarting, setIsStarting] = useState(false)
  const batch = batchFor(card)
  const selected = fullListCard === card ? selection : []
  const firstOffer = card === CcqCards.NOT_IN_RECORDS ? CCQ_AI_FOOTER_DONE_NOT_IN_RECORDS : CCQ_AI_FOOTER_DONE

  const handleStarted = (evaluation: CrossClientQueryEvaluation) => {
    trackEvaluation(evaluation, card)
  }

  const runSelection = async () => {
    setIsStarting(true)
    try {
      const outcome = await evaluateSelectionSplit(selected)
      if (outcome.aiBlockedReason === 'account-unavailable') {
        dispatch(addToast({ variant: 'error', title: 'Please try again', messages: [CROSS_CLIENT_AI_PHASE_ACCESS_LOADING_MESSAGE] }))
      } else if (outcome.aiBlockedReason === 'not-permitted') {
        const message = hasAdminAccess ? CROSS_CLIENT_AI_PHASE_ADMIN_BLOCKED_MESSAGE : CROSS_CLIENT_AI_PHASE_PERMISSION_BLOCKED_MESSAGE
        dispatch(addToast({ variant: 'error', title: CROSS_CLIENT_AI_PHASE_BLOCKED_TITLE, messages: [message] }))
      }
      if (outcome.evaluation) {
        trackEvaluation(outcome.evaluation, card, outcome.sentResultIds)
        // The selected clients are leaving the card; left ticked, the next run or action would target them.
        clearSelection()
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsStarting(false)
    }
  }

  const runButton =
    selected.length > 0 ? (
      <Button variant='header' className={SECONDARY} onClick={() => void runSelection()} loading={isStarting} disabled={isBlocked && !canRetryAccess}>
        {CCQ_AI_RUN}
      </Button>
    ) : (
      <OverlayTrigger
        trigger='click'
        rootClose
        placement='top-end'
        show={isPickerOpen}
        onToggle={setIsPickerOpen}
        overlay={
          <Popover id={`ccq-ai-popover-${card}`} className={styles.aiPopover}>
            <Popover.Body>
              <CrossClientQuickActionPopover card={card} onClose={() => setIsPickerOpen(false)} onStarted={handleStarted} />
            </Popover.Body>
          </Popover>
        }
      >
        <Button variant='header' className={SECONDARY} disabled={isBlocked}>
          {CCQ_AI_RUN}
        </Button>
      </OverlayTrigger>
    )

  const content = (() => {
    if (!isDone) {
      return {
        message: <span className='paragraph-small bold zocks-text-gray-600'>{CCQ_AI_FOOTER_RUNNING}</span>,
        buttons: (
          <Button variant='header' className={SECONDARY} disabled>
            {CCQ_AI_RUN}
          </Button>
        ),
      }
    }
    if (batch.step === CcqAiBatchSteps.RUNNING) {
      return {
        message: (
          <span className='d-flex align-items-center gap-2 paragraph-small bold zocks-text-gray-900'>
            <CcqAiIndicator />
            {ccqAiBatchRunning(batch.clientCount)}
            {batch.isStopping ? (
              <span className='paragraph-small zocks-text-gray-600'>{CCQ_AI_STOPPING}</span>
            ) : (
              <Button variant='primaryLinkButton' className='button-small p-0 border-0' onClick={() => void stopBatch(card)}>
                {CCQ_AI_STOP}
              </Button>
            )}
          </span>
        ),
        buttons: null,
      }
    }
    if (batch.step === CcqAiBatchSteps.RESULT) {
      return {
        message: (
          <span className='paragraph-small bold zocks-text-gray-900'>
            {ccqAiBatchResult({
              checked: batch.clientCount,
              toMatched: batch.movedToMatched,
              toNotMatched: batch.movedToNotMatched,
              toMissingData: batch.movedToMissingData,
            })}
          </span>
        ),
        buttons: null,
      }
    }
    return {
      message: <span className='paragraph-small bold zocks-text-gray-600'>{firstOffer}</span>,
      buttons: (
        <Tooltip placement='top' title={tooltipTitle} tooltipContent={tooltipContent}>
          {runButton}
        </Tooltip>
      ),
    }
  })()

  return (
    <div
      className={classNames(
        styles.footer,
        'd-flex align-items-center gap-3 zocks-py-0.75 zocks-pl-2.75 pe-3 border-top zocks-border-color-gray-200 zocks-bg-color-gray-100',
      )}
    >
      <div className='flex-grow-1 mw-0' role='status'>
        {content.message}
      </div>
      {content.buttons ? <div className='d-flex align-items-center gap-2 flex-shrink-0'>{content.buttons}</div> : null}
    </div>
  )
}
